import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import {
  MapPin, Search, Plus, Trash2, Save, X, Wind,
  Loader2, Navigation, Edit3, Move, ChevronRight, ChevronLeft,
  Building2, Home, Bath, Warehouse
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Marker types configuration
const MARKER_TYPES = {
  turbine: {
    name: 'Turbine',
    icon: Wind,
    color: '#22c55e',
    newColor: '#3b82f6',
    prefix: 'T',
    canAdd: false  // Turbines are defined in project, cannot be added here
  },
  building: {
    name: 'Building',
    icon: Building2,
    color: '#8b5cf6',
    newColor: '#a78bfa',
    prefix: 'B',
    canAdd: true
  },
  administration: {
    name: 'Administration',
    icon: Home,
    color: '#f59e0b',
    newColor: '#fbbf24',
    prefix: 'A',
    canAdd: true
  },
  toilet: {
    name: 'Toilet',
    icon: Bath,
    color: '#06b6d4',
    newColor: '#22d3ee',
    prefix: 'WC',
    canAdd: true
  },
  warehouse: {
    name: 'Warehouse',
    icon: Warehouse,
    color: '#64748b',
    newColor: '#94a3b8',
    prefix: 'W',
    canAdd: true
  }
};

// Types that can be added via map click
const ADDABLE_MARKER_TYPES = Object.entries(MARKER_TYPES)
  .filter(([_, config]) => config.canAdd)
  .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create marker icon based on type
const createMarkerIcon = (type, label, isNew = false, isSelected = false) => {
  const config = MARKER_TYPES[type] || MARKER_TYPES.turbine;
  const bgColor = isNew ? config.newColor : config.color;
  const borderColor = isSelected ? '#fbbf24' : 'white';
  const borderWidth = isSelected ? '4px' : '3px';

  // SVG icons for each type
  const icons = {
    turbine: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"/><path d="M9.6 4.6A2 2 0 1 1 11 8H2"/><path d="M12.6 19.4A2 2 0 1 0 14 16H2"/></svg>`,
    building: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>`,
    administration: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
    toilet: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/><line x1="10" x2="8" y1="5" y2="7"/><line x1="2" x2="22" y1="12" y2="12"/><line x1="7" x2="7" y1="19" y2="21"/><line x1="17" x2="17" y1="19" y2="21"/></svg>`,
    warehouse: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z"/><path d="M6 18h12"/><path d="M6 14h12"/><rect width="12" height="12" x="6" y="10"/></svg>`
  };

  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${bgColor};
        min-width: 32px;
        height: 32px;
        padding: 0 6px;
        border-radius: ${type === 'turbine' ? '16px' : '8px'};
        border: ${borderWidth} solid ${borderColor};
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
        color: white;
        font-weight: bold;
        font-size: 10px;
        white-space: nowrap;
        cursor: ${isSelected ? 'grab' : 'pointer'};
      ">
        ${icons[type] || icons.turbine}
        <span>${label}</span>
      </div>
    `,
    className: 'editor-marker',
    iconSize: [50, 38],
    iconAnchor: [25, 19]
  });
};

// Map click handler component
const MapClickHandler = ({ onMapClick, canAdd }) => {
  useMapEvents({
    click: (e) => {
      if (canAdd) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

// Map controller for programmatic control
const MapController = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);

  return null;
};

// Draggable marker component
const DraggableMarker = ({ marker, onDragEnd, onSelect, isSelected, label }) => {
  const markerRef = useRef(null);
  const config = MARKER_TYPES[marker.marker_type] || MARKER_TYPES.turbine;
  const [position, setPosition] = useState([parseFloat(marker.latitude), parseFloat(marker.longitude)]);

  // Store callbacks in refs to avoid recreating eventHandlers
  const onDragEndRef = useRef(onDragEnd);
  const onSelectRef = useRef(onSelect);
  const markerIdRef = useRef(marker.id);

  // Update refs when props change
  useEffect(() => {
    onDragEndRef.current = onDragEnd;
    onSelectRef.current = onSelect;
    markerIdRef.current = marker.id;
  }, [onDragEnd, onSelect, marker.id]);

  // Sync position when marker props change (e.g., after save/reload)
  useEffect(() => {
    const newPos = [parseFloat(marker.latitude), parseFloat(marker.longitude)];
    setPosition(newPos);
  }, [marker.latitude, marker.longitude]);

  // Memoize event handlers to prevent React-Leaflet from losing events
  const eventHandlers = useMemo(() => ({
    dragend() {
      const markerEl = markerRef.current;
      if (markerEl != null) {
        const newPos = markerEl.getLatLng();
        // Update local position immediately
        setPosition([newPos.lat, newPos.lng]);
        // Notify parent using ref to get latest callback
        if (onDragEndRef.current) {
          onDragEndRef.current(markerIdRef.current, newPos.lat, newPos.lng);
        }
      }
    },
    click() {
      if (onSelectRef.current) {
        onSelectRef.current(markerIdRef.current);
      }
    }
  }), []); // Empty deps - handlers use refs which are always current

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={createMarkerIcon(marker.marker_type || 'turbine', label, marker.isNew, isSelected)}
    >
      <Popup>
        <div className="p-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: config.color }}>
              {config.name}
            </span>
          </div>
          <div className="font-semibold">{marker.name || marker.turbine_number || `${config.name} ${marker.id}`}</div>
          <div className="text-xs text-gray-500 mt-1">
            Lat: {parseFloat(marker.latitude).toFixed(6)}<br />
            Lng: {parseFloat(marker.longitude).toFixed(6)}
          </div>
          {marker.isNew && (
            <div className="text-xs text-blue-600 mt-1 font-medium">New - not saved</div>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

const TurbineMapEditor = ({ selectedProject, onClose }) => {
  const [markers, setMarkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(selectedProject || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mapCenter, setMapCenter] = useState([52.0, 19.0]);
  const [mapZoom, setMapZoom] = useState(6);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [counters, setCounters] = useState({ building: 1, administration: 1, toilet: 1, warehouse: 1 });
  const [projectName, setProjectName] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedMarkerType, setSelectedMarkerType] = useState('building');
  const [filterType, setFilterType] = useState('all');

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
        const response = await axios.get(`${API_BASE_URL}/api/projects/`, { headers });
        setProjects(response.data);

        if (selectedProject) {
          const proj = response.data.find(p => p.id.toString() === selectedProject.toString());
          if (proj) setProjectName(proj.name);
        }
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    };
    loadProjects();
  }, [selectedProject]);

  // Load markers for selected project
  useEffect(() => {
    const loadMarkers = async () => {
      if (!currentProject) {
        setMarkers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

        const projectResponse = await axios.get(`${API_BASE_URL}/api/projects/${currentProject}/`, { headers });
        const projName = projectResponse.data.name;
        setProjectName(projName);

        // Load turbines from ProjectTurbine API
        let projectTurbines = [];
        try {
          const turbinesResponse = await axios.get(`${API_BASE_URL}/api/project-turbines/?project=${currentProject}`, { headers });
          projectTurbines = turbinesResponse.data.map(t => ({
            ...t,
            // Add name for display compatibility
            name: `T${t.turbine_number}${t.turbine_id ? ` (${t.turbine_id})` : ''}`,
            marker_type: 'turbine',
            isNew: false
          }));
        } catch (e) {
          console.log('ProjectTurbine API not available');
        }

        // Load project markers (buildings, admin, toilets, etc.)
        let projectMarkers = [];
        try {
          const markersResponse = await axios.get(`${API_BASE_URL}/api/project-markers/?project=${currentProject}`, { headers });
          projectMarkers = markersResponse.data.map(m => ({ ...m, isNew: false }));
        } catch (e) {
          // API might not exist yet, continue without project markers
          console.log('Project markers API not available');
        }

        const allMarkers = [...projectTurbines, ...projectMarkers];
        setMarkers(allMarkers);

        const withCoords = allMarkers.filter(m => m.latitude && m.longitude);
        if (withCoords.length > 0) {
          setMapCenter([parseFloat(withCoords[0].latitude), parseFloat(withCoords[0].longitude)]);
          setMapZoom(13);
        }

        // Calculate counters for addable types only (not turbine)
        const newCounters = { building: 1, administration: 1, toilet: 1, warehouse: 1 };
        allMarkers.forEach(m => {
          const type = m.marker_type || 'turbine';
          if (type === 'turbine') return; // Skip turbines for counter calculation
          const name = m.name || '';
          const match = name.match(/(\d+)/);
          if (match) {
            const num = parseInt(match[1]);
            if (newCounters[type] !== undefined && num >= newCounters[type]) {
              newCounters[type] = num + 1;
            }
          }
        });
        setCounters(newCounters);

      } catch (error) {
        console.error('Error loading markers:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMarkers();
  }, [currentProject]);

  // Search for location
  const searchLocation = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching location:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleLocationSelect = (result) => {
    setMapCenter([parseFloat(result.lat), parseFloat(result.lon)]);
    setMapZoom(14);
    setSearchResults([]);
    setSearchQuery(result.display_name.split(',')[0]);
  };

  const handleMapClick = (latlng) => {
    if (!currentProject) return;

    // If a marker is selected, move it to the clicked location
    if (selectedMarker) {
      const markerToMove = markers.find(m => m.id === selectedMarker);
      if (markerToMove) {
        // Move the selected marker to the clicked location
        setMarkers(prev => prev.map(m =>
          m.id === selectedMarker
            ? { ...m, latitude: latlng.lat, longitude: latlng.lng, isModified: true }
            : m
        ));
        setHasChanges(true);
        return;
      }
    }

    // No marker selected - add a new marker of the selected type (not turbines)
    const config = MARKER_TYPES[selectedMarkerType];
    const counter = counters[selectedMarkerType];

    const newMarker = {
      id: `new-${Date.now()}`,
      marker_type: selectedMarkerType,
      name: `${projectName} ${config.prefix}${String(counter).padStart(2, '0')}`,
      latitude: latlng.lat,
      longitude: latlng.lng,
      project: currentProject,
      isNew: true
    };

    setMarkers(prev => [...prev, newMarker]);
    setCounters(prev => ({ ...prev, [selectedMarkerType]: prev[selectedMarkerType] + 1 }));
    setHasChanges(true);
    setSelectedMarker(newMarker.id);
  };

  const handleMarkerDrag = useCallback((markerId, newLat, newLng) => {
    console.log('handleMarkerDrag called:', markerId, newLat, newLng);
    setMarkers(prev => prev.map(m =>
      m.id === markerId
        ? { ...m, latitude: newLat, longitude: newLng, isModified: true }
        : m
    ));
    setHasChanges(true);
  }, []);

  const handleMarkerSelect = useCallback((markerId) => {
    setSelectedMarker(prev => prev === markerId ? null : markerId);
  }, []);

  const deleteMarker = () => {
    if (!selectedMarker) return;

    const marker = markers.find(m => m.id === selectedMarker);
    if (!marker) return;

    // Cannot delete turbines - the count is defined in project settings
    if (marker.marker_type === 'turbine') {
      alert('Turbines cannot be deleted here. The number of turbines is defined in project settings. You can move turbines by selecting them and clicking on the map.');
      return;
    }

    if (marker.isNew) {
      setMarkers(prev => prev.filter(m => m.id !== selectedMarker));
    } else {
      setMarkers(prev => prev.map(m =>
        m.id === selectedMarker ? { ...m, isDeleted: true } : m
      ));
    }
    setSelectedMarker(null);
    setHasChanges(true);
  };

  const updateMarkerName = (markerId, newName) => {
    setMarkers(prev => prev.map(m =>
      m.id === markerId
        ? { ...m, name: newName, turbine_number: m.marker_type === 'turbine' ? newName : m.turbine_number, isModified: true }
        : m
    ));
    setHasChanges(true);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

      for (const marker of markers) {
        const isTurbine = marker.marker_type === 'turbine';

        if (isTurbine) {
          // Turbines: only save position changes (cannot add or delete)
          if (marker.isModified && !marker.isDeleted) {
            try {
              // Format lat/lng to max 7 decimal places as required by the ProjectTurbine model
              const lat = parseFloat(marker.latitude).toFixed(7);
              const lng = parseFloat(marker.longitude).toFixed(7);
              console.log('Saving turbine position:', marker.id, lat, lng);
              const response = await axios.patch(`${API_BASE_URL}/api/project-turbines/${marker.id}/`, {
                latitude: lat,
                longitude: lng
              }, { headers });
              console.log('Turbine saved:', response.data);
            } catch (e) {
              console.error('Could not update turbine position:', e.response?.data || e.message);
              alert(`Error saving turbine ${marker.name || marker.turbine_number}: ${JSON.stringify(e.response?.data) || e.message}`);
            }
          }
        } else {
          // Other markers: full CRUD (building, administration, toilet, warehouse)
          if (marker.isDeleted && !marker.isNew) {
            try {
              console.log('Deleting marker:', marker.id);
              await axios.delete(`${API_BASE_URL}/api/project-markers/${marker.id}/`, { headers });
              console.log('Marker deleted');
            } catch (e) {
              console.error('Could not delete project marker:', e.response?.data || e.message);
              alert(`Error deleting ${marker.name}: ${JSON.stringify(e.response?.data) || e.message}`);
            }
          } else if (marker.isNew && !marker.isDeleted) {
            try {
              const lat = parseFloat(marker.latitude).toFixed(6);
              const lng = parseFloat(marker.longitude).toFixed(6);
              console.log('Creating marker:', marker.marker_type, marker.name, lat, lng);
              const response = await axios.post(`${API_BASE_URL}/api/project-markers/`, {
                project: parseInt(currentProject),
                marker_type: marker.marker_type,
                name: marker.name,
                latitude: lat,
                longitude: lng
              }, { headers });
              console.log('Marker created:', response.data);
            } catch (e) {
              console.error('Could not create project marker:', e.response?.data || e.message);
              alert(`Error creating ${marker.name}: ${JSON.stringify(e.response?.data) || e.message}`);
            }
          } else if (marker.isModified && !marker.isDeleted) {
            try {
              const lat = parseFloat(marker.latitude).toFixed(6);
              const lng = parseFloat(marker.longitude).toFixed(6);
              console.log('Updating marker:', marker.id, marker.name, lat, lng);
              const response = await axios.patch(`${API_BASE_URL}/api/project-markers/${marker.id}/`, {
                name: marker.name,
                latitude: lat,
                longitude: lng
              }, { headers });
              console.log('Marker updated:', response.data);
            } catch (e) {
              console.error('Could not update project marker:', e.response?.data || e.message);
              alert(`Error updating ${marker.name}: ${JSON.stringify(e.response?.data) || e.message}`);
            }
          }
        }
      }

      // Reload all markers
      let projectTurbines = [];
      try {
        const turbinesResponse = await axios.get(`${API_BASE_URL}/api/project-turbines/?project=${currentProject}`, { headers });
        projectTurbines = turbinesResponse.data.map(t => ({
          ...t,
          name: `T${t.turbine_number}${t.turbine_id ? ` (${t.turbine_id})` : ''}`,
          marker_type: 'turbine',
          isNew: false,
          isModified: false
        }));
      } catch (e) {
        console.log('Could not reload project turbines:', e.message);
      }

      let projectMarkers = [];
      try {
        const markersResponse = await axios.get(`${API_BASE_URL}/api/project-markers/?project=${currentProject}`, { headers });
        projectMarkers = markersResponse.data.map(m => ({ ...m, isNew: false, isModified: false }));
      } catch (e) {
        // Keep non-turbine markers that were added locally
        projectMarkers = markers.filter(m => m.marker_type !== 'turbine' && !m.isDeleted).map(m => ({ ...m, isNew: false, isModified: false }));
      }

      setMarkers([...projectTurbines, ...projectMarkers]);
      setHasChanges(false);
      alert('Changes saved successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getMarkerLabel = (marker) => {
    const config = MARKER_TYPES[marker.marker_type] || MARKER_TYPES.turbine;
    const name = marker.name || marker.turbine_number || '';
    const match = name.match(/(\d+)/);
    return match ? match[1] : '?';
  };

  const visibleMarkers = markers.filter(m => !m.isDeleted);
  const filteredMarkers = filterType === 'all'
    ? visibleMarkers
    : visibleMarkers.filter(m => m.marker_type === filterType);
  const selectedMarkerData = markers.find(m => m.id === selectedMarker);

  return (
    <div className="flex flex-col bg-background rounded-lg border border-border overflow-hidden" style={{ height: '100%' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Edit3 className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-bold">Project Map Editor</h2>
              <div className="flex items-center gap-2 text-xs text-white/70">
                <span>{projectName || 'Select project'}</span>
                <span>•</span>
                {Object.entries(MARKER_TYPES).map(([type, config]) => {
                  const count = visibleMarkers.filter(m => m.marker_type === type).length;
                  if (count === 0) return null;
                  return (
                    <span key={type} className="flex items-center gap-0.5">
                      {count} {config.prefix}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full">
                Unsaved changes
              </span>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex" style={{ flex: '1 1 0', minHeight: 0 }}>
        {/* Left Sidebar - Collapsible */}
        <div className={`bg-gray-50 border-r border-gray-200 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'w-12' : 'w-72'}`}>
          {/* Collapse toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 hover:bg-gray-100 border-b border-gray-200 flex items-center justify-center flex-shrink-0"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          {!sidebarCollapsed && (
            <div className="flex flex-col" style={{ flex: '1 1 0', minHeight: 0 }}>
              {/* Scrollable content area */}
              <div className="overflow-y-auto" style={{ flex: '1 1 0', minHeight: 0 }}>
                {/* Project Selector */}
                <div className="p-3 border-b border-gray-200">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
                  <select
                    value={currentProject}
                    onChange={(e) => {
                      if (hasChanges && !window.confirm('You have unsaved changes. Continue?')) return;
                      setCurrentProject(e.target.value);
                      setHasChanges(false);
                    }}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select project...</option>
                    {projects.map(proj => (
                      <option key={proj.id} value={proj.id}>
                        {proj.project_number} - {proj.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Search */}
                <div className="p-3 border-b border-gray-200">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Search Location</label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchLocation()}
                        placeholder="Enter address or place..."
                        className="w-full text-sm border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:ring-2 focus:ring-emerald-500"
                      />
                      <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    </div>
                    <button
                      onClick={searchLocation}
                      disabled={searching}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                    </button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleLocationSelect(result)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="font-medium text-gray-900 truncate">{result.display_name.split(',')[0]}</div>
                          <div className="text-xs text-gray-500 truncate">{result.display_name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Marker Type Selector - Click on map to add */}
                <div className="p-3 border-b border-gray-200">
                  <label className="block text-xs font-medium text-gray-500 mb-2">Click on map to add:</label>
                  <div className="grid grid-cols-2 gap-1">
                    {Object.entries(ADDABLE_MARKER_TYPES).map(([type, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={type}
                          onClick={() => {
                            setSelectedMarkerType(type);
                            setSelectedMarker(null); // Deselect any selected marker so clicking on map adds new one
                          }}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg text-xs font-medium transition-colors ${
                            selectedMarkerType === type
                              ? 'text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          style={selectedMarkerType === type ? { backgroundColor: config.color } : {}}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="truncate w-full text-center">{config.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Delete Tool */}
                <div className="p-3 border-b border-gray-200">
                  <button
                    onClick={deleteMarker}
                    disabled={!selectedMarker}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Selected
                  </button>
                </div>

                {/* Marker List */}
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-500">
                      Markers ({filteredMarkers.length})
                    </label>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="all">All types</option>
                      {Object.entries(MARKER_TYPES).map(([type, config]) => (
                        <option key={type} value={type}>{config.name}</option>
                      ))}
                    </select>
                  </div>

                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : filteredMarkers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No markers</p>
                      <p className="text-xs mt-1">Select type, enable add mode and click on map</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredMarkers.map(marker => {
                        const config = MARKER_TYPES[marker.marker_type] || MARKER_TYPES.turbine;
                        const Icon = config.icon;
                        return (
                          <div
                            key={marker.id}
                            onClick={() => handleMarkerSelect(marker.id)}
                            className={`p-2 rounded-lg border cursor-pointer transition-colors ${
                              selectedMarker === marker.id
                                ? 'border-amber-400 bg-amber-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-7 h-7 rounded flex items-center justify-center text-white"
                                style={{ backgroundColor: marker.isNew ? config.newColor : config.color }}
                              >
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <input
                                  type="text"
                                  value={marker.name || marker.turbine_number || ''}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    updateMarkerName(marker.id, e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full text-sm font-medium bg-transparent border-0 p-0 focus:ring-0"
                                />
                                <div className="text-xs text-gray-500">
                                  {marker.latitude && marker.longitude ? (
                                    `${parseFloat(marker.latitude).toFixed(4)}, ${parseFloat(marker.longitude).toFixed(4)}`
                                  ) : (
                                    <span className="text-amber-600">No coordinates</span>
                                  )}
                                </div>
                              </div>
                              {marker.isNew && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">New</span>
                              )}
                              {marker.isModified && !marker.isNew && (
                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 text-xs rounded">Mod</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <div className="p-3 border-t border-gray-200 bg-white flex-shrink-0">
                <button
                  onClick={saveChanges}
                  disabled={!hasChanges || saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          {!currentProject ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <Wind className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">Select a Project</h3>
                <p className="text-sm text-gray-500">Choose a project from the list to edit turbines</p>
              </div>
            </div>
          ) : (
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
              className="cursor-crosshair"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapController center={mapCenter} zoom={mapZoom} />
              <MapClickHandler onMapClick={handleMapClick} canAdd={!!currentProject} />

              {visibleMarkers
                .filter(m => m.latitude && m.longitude)
                .map(marker => (
                  <DraggableMarker
                    key={marker.id}
                    marker={marker}
                    onDragEnd={handleMarkerDrag}
                    onSelect={handleMarkerSelect}
                    isSelected={selectedMarker === marker.id}
                    label={getMarkerLabel(marker)}
                  />
                ))}
            </MapContainer>
          )}

          {/* Map Instructions Overlay */}
          {currentProject && (
            <div
              className="absolute top-4 left-1/2 -translate-x-1/2 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-[1000]"
              style={{ backgroundColor: selectedMarker
                ? '#f59e0b'
                : MARKER_TYPES[selectedMarkerType].color
              }}
            >
              {selectedMarker ? (
                <>
                  <Move className="w-4 h-4" />
                  <span className="text-sm font-medium">Click to move selected marker</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span className="text-sm font-medium">Click to add {MARKER_TYPES[selectedMarkerType].name}</span>
                </>
              )}
            </div>
          )}

          {/* Selected Marker Info */}
          {selectedMarkerData && (
            <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 min-w-56 z-[1000]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center text-white"
                    style={{ backgroundColor: MARKER_TYPES[selectedMarkerData.marker_type || 'turbine'].color }}
                  >
                    {React.createElement(MARKER_TYPES[selectedMarkerData.marker_type || 'turbine'].icon, { className: 'w-3 h-3' })}
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {MARKER_TYPES[selectedMarkerData.marker_type || 'turbine'].name}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedMarker(null)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <div className="text-sm text-gray-600">
                <div><strong>Name:</strong> {selectedMarkerData.name || selectedMarkerData.turbine_number}</div>
                <div><strong>Lat:</strong> {parseFloat(selectedMarkerData.latitude).toFixed(6)}</div>
                <div><strong>Lng:</strong> {parseFloat(selectedMarkerData.longitude).toFixed(6)}</div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                <Move className="w-3 h-3 inline mr-1" />
                Drag marker to change position
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TurbineMapEditor;
